import Button from '@creator/ui/components/Button/ButtonV2';
import Text from '@creator/ui/components/Text/Text';
import { cn } from '@creator/ui/utils/ui';
import HorizontalScrollable from '@src/basic-components/HorizontalScrollable/HorizontalScrollable';
import Icon from '@src/basic-components/Icon/Icon';
import PostCard from '@src/components/Post/PostCard/PostCard';
import { usePaginatedPosts } from '@src/store/posts/hooks/usePaginatedPosts';
import type { FC } from 'react';
import { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export interface ChildPostsListProps {
    tokenName: string;
    parentPostId: string;
    className?: string;
}

const ChildPostsList: FC<ChildPostsListProps> = props => {
    const { tokenName, parentPostId, className = '' } = props;
    const { t } = useTranslation();

    const { postIds, loadMore, loadStatus, hasMore } = usePaginatedPosts({
        type: 'childPosts',
        tokenName,
        parentPostId,
        fetchLimit: 20,
        includeMyReactions: true,
    });

    useEffect(() => {
        if (postIds.length > 0) return;
        loadMore();
    }, [parentPostId]);

    function renderPost(postId: string) {
        return (
            <div key={postId} className="shrink-0 w-[236px]">
                <PostCard disableMenuPopover={true} tokenName={tokenName} postId={postId} className="max-h-[180px] overflow-hidden rounded-lg" />
            </div>
        );
    }

    function renderLoadMoreButton() {
        if (!hasMore) return null;

        return (
            <Button isLoading={loadStatus === 'loading'} variant="filled" color="primary-alternative" size="md" type="round" onClick={loadMore}>
                <Icon name="plus" />
            </Button>
        );
    }

    function renderContent() {
        if (postIds.length === 0 && loadStatus === 'success') return null;

        return (
            <div className={cn('', className)}>
                <div className="flex items-center px-4 pt-1 pb-3 space-x-2 text-sm">
                    <Text className="font-bold">{t('postTab.childPosts.title')}</Text>
                </div>
                <HorizontalScrollable scrollOffset={236 + 12} scrollContainerClassname="px-4 space-x-3 pb-4" arrowClassName="z-10 shadow-lg">
                    {postIds.map(renderPost)}
                    {renderLoadMoreButton()}
                </HorizontalScrollable>
            </div>
        );
    }

    return renderContent();
};

export default memo(ChildPostsList);
